import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileUp, Loader2, Plus } from 'lucide-react';
import { customersAPI, inquiriesAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

export default function InquiryCreatePage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    customer_id: '', customer_inquiry_number: '', customer_inquiry_date: '',
    target_market: 'domestic', remarks: '', products: [],
  });

  useEffect(() => {
    customersAPI.list({ is_active: true }).then((response) => {
      setCustomers(response.data?.items || response.data || []);
    }).catch(() => toast.error('Failed to load customers'));
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    if (!form.customer_id) return toast.error('Select a customer');
    setSaving(true);
    try {
      const response = await inquiriesAPI.create({
        ...form,
        customer_inquiry_date: form.customer_inquiry_date || null,
      });
      for (const file of files) {
        await inquiriesAPI.uploadDocument(response.data.id, file);
      }
      toast.success(`Inquiry ${response.data.inquiry_number} created`);
      navigate(`/inquiries/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create inquiry');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-5xl space-y-8 animate-fade-in" data-testid="create-inquiry-page">
      <header className="border-b pb-6">
        <Button variant="ghost" className="mb-4 px-0" onClick={() => navigate('/inquiries')} data-testid="create-inquiry-back-button">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to inquiries
        </Button>
        <h1 className="text-4xl sm:text-5xl font-heading font-bold" data-testid="create-inquiry-heading">Create inquiry</h1>
      </header>

      <form onSubmit={submit} className="space-y-8">
        <section className="border bg-white p-6">
          <h2 className="mb-6 text-lg font-heading font-bold">Customer & reference</h2>
          <div className="grid gap-5 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Customer *</Label>
              <Select value={form.customer_id} onValueChange={(value) => setForm({ ...form, customer_id: value })}>
                <SelectTrigger data-testid="create-inquiry-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>{customers.map((customer) => <SelectItem key={customer.id} value={customer.id}>{customer.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Customer inquiry reference</Label>
              <Input value={form.customer_inquiry_number} onChange={(event) => setForm({ ...form, customer_inquiry_number: event.target.value })} data-testid="create-inquiry-reference-input" />
            </div>
            <div className="space-y-2"><Label>Inquiry date</Label>
              <Input type="date" value={form.customer_inquiry_date} onChange={(event) => setForm({ ...form, customer_inquiry_date: event.target.value })} data-testid="create-inquiry-date-input" />
            </div>
            <div className="space-y-2"><Label>Market</Label>
              <Select value={form.target_market} onValueChange={(value) => setForm({ ...form, target_market: value })}>
                <SelectTrigger data-testid="create-inquiry-market-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="domestic">Domestic</SelectItem><SelectItem value="export">Export</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
        </section>

        <section className="border bg-white p-6">
          <h2 className="mb-2 text-lg font-heading font-bold">Original documents</h2>
          <p className="mb-5 text-sm text-muted-foreground">Attach PDFs, spreadsheets, Word files, images, and drawings. More files can be added later.</p>
          <label className="flex cursor-pointer flex-col items-center justify-center border-2 border-dashed p-10 text-center hover:border-blue-500" data-testid="create-inquiry-document-dropzone">
            <FileUp className="mb-3 h-8 w-8 text-blue-600" />
            <span className="font-medium">Choose one or more files</span>
            <span className="mt-1 text-sm text-muted-foreground">Maximum 25 MB per file</span>
            <input type="file" multiple className="hidden" accept=".pdf,.xlsx,.xls,.csv,.doc,.docx,.txt,.jpg,.jpeg,.png,.webp,.heic,.dwg,.dxf"
              onChange={(event) => setFiles(Array.from(event.target.files || []))} data-testid="create-inquiry-file-input" />
          </label>
          {files.length > 0 && <p className="mt-3 text-sm font-medium" data-testid="create-inquiry-file-count">{files.length} file(s) selected</p>}
        </section>

        <section className="border bg-white p-6">
          <Label>Remarks</Label>
          <Textarea className="mt-2" rows={4} value={form.remarks} onChange={(event) => setForm({ ...form, remarks: event.target.value })} data-testid="create-inquiry-remarks-input" />
        </section>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/inquiries')} data-testid="create-inquiry-cancel-button">Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="create-inquiry-submit-button">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
            {saving ? 'Creating…' : 'Create inquiry'}
          </Button>
        </div>
      </form>
    </div>
  );
}